/**
 * save/serialize.ts — convert a live GameState into a JSON-safe SaveBlob.
 *
 * Per MULTIPLAYER §6.1 (v2M2): v1 single-player saves are JSON; the
 * MP-era binary format will sit behind the same function pair so call
 * sites don't need to change.
 *
 * The serialiser drops the three static catalogues (shipTemplates,
 * weapons, techs) — those live in ships.json + tech-tree.json shipped
 * with the game and are re-attached on load from a freshly-loaded
 * LoadedGameData. This keeps saves small (a typical mid-game save
 * comes in well under 1MB) and protects against catalogue drift between
 * sessions: if the spreadsheets ship a balance change, every save
 * inherits it on load rather than freezing the old numbers in place.
 *
 * Map / Set instances serialise as arrays. The deserialiser
 * (save/deserialize.ts) is the inverse.
 */

import type {
  BeamFire,
  Empire,
  EnvironmentEntity,
  GameState,
  Planet,
  PlanetFoWState,
  Projectile,
  ShipInstance,
  StarSystem,
  Structure,
  StructureFoWState,
  VisibilityRecord,
  WarpGateLink,
} from '../state/types/index';
import { EMPIRE_NEUTRAL_ID } from '../state/types/index';
import type { RngState } from '../state/rng';

/**
 * Sentinel for `WeaponSlotState.ammoRemaining === Infinity`. Non-Concussion
 * weapons carry Infinity ammo (MECHANICS §3); JSON.stringify silently turns
 * Infinity into null, so we substitute a reserved negative-one in the
 * serialised form. The deserialiser restores Infinity. ammoRemaining is
 * otherwise non-negative, so -1 is a safe sentinel.
 */
const INF_AMMO_SENTINEL = -1;

/**
 * Save-blob format version. Bump this on any breaking change to the
 * SaveBlob shape; deserialiser refuses mismatched versions with a
 * helpful error rather than silently producing a broken state.
 *
 * Distinct from `state.version` (SAVE_FORMAT_VERSION in types.ts),
 * which versions the live GameState shape itself. The two will
 * usually move in lockstep but are conceptually independent — a
 * blob layout change (e.g. adding a header field) doesn't require
 * a state-shape bump.
 */
/**
 * MFix1.4 bumped this from 1 → 2 alongside SAVE_FORMAT_VERSION when
 * PlanetSize switched to a number. deserialize.ts checks this on
 * load and throws SaveLoadError ("Saves can't be migrated
 * automatically") on mismatch.
 *
 * MFix3.4 bumped 2 → 3 alongside SAVE_FORMAT_VERSION when
 * `pendingColoniseTargetId` was added to ShipInstance.
 *
 * v3M7.1 bumped 3 → 4 alongside SAVE_FORMAT_VERSION when
 * `aiDifficulty` + `aiBehaviour` were added to Empire.
 *
 * v3M2.2b bumped 4 → 5 covering ODP ring state + Minefield runtime.
 *
 * MFix6.3 bumped 5 → 6 alongside SAVE_FORMAT_VERSION when
 * `scoutedSystemIds` was added to Empire (galaxy-view fog-of-war).
 *
 * v3M18.1 bumped 6 → 7 alongside SAVE_FORMAT_VERSION when
 * `warpGateLinks` was added to GameState.
 *
 * v3M18.5 bumped 7 → 8 alongside SAVE_FORMAT_VERSION when
 * `jumpTargetGateId` was added to ShipInstance for warp-link arrival.
 *
 * v3M18.5 fix-up bumped 8 → 9 alongside SAVE_FORMAT_VERSION when
 * `pendingWarpGateLinkId` was added to ShipInstance for sail-then-jump.
 *
 * v3M17.1 bumped 9 → 10 alongside SAVE_FORMAT_VERSION for the carrier
 * fighter launch data model — six new optional ShipInstance fields
 * (carrier embarked-squadron state + per-carrier behaviour settings +
 * squadron tracking). Optional fields round-trip via the existing
 * `serializeShip` `...spread` path, no fixture sweep, but the version
 * bump signals the schema delta to consumers reading saves.
 *
 * v3M22.1 bumped 10 → 11 for the ship-commands data model — four new
 * required ShipInstance fields (`commandKind`, `waypoints`,
 * `guardTargetKind`, `guardTargetId`). Required (not optional) so the
 * sim never has to defend against absent fields; ~30-file fixture
 * sweep covers existing test fixtures.
 *
 * v3M21.1 bumped 11 → 12 for the diplomacy data model
 * (`DIPLOMACY.md §11`). New fields land all at once so subsequent
 * v3M21.N sub-checkpoints don't re-bump:
 *   - `Empire.diplomaticFocus: PlanetId | null`
 *   - `Planet.influence: Map<EmpireId, number>` (serialised as a record)
 *   - `Planet.federatedWith: EmpireId | null`
 *   - `Planet.tradingWith: EmpireId[]`
 *   - `Planet.vassalOf: EmpireId | null`
 *   - `state.empires` includes a synthetic neutral empire row at
 *     `EMPIRE_NEUTRAL_ID = 0`.
 * Required (not optional) so the sim never defends against absent
 * fields. Pre-v12 saves are hard-rejected per the established pattern.
 */
/**
 * v3M28.1 — bumped 12 → 13 when `state.missiles: Map<MissileId, Missile>`
 * was added. Serialised as a flat array of `[id, Missile]` pairs and
 * restored back to a Map on deserialise. Pre-v13 saves are hard-
 * rejected per the established pattern (no migration).
 */
/**
 * v4M5.1 — bumped 13 → 14 to enforce the `docs/HYPERSPACE.md §7` hard
 * break: pre-v4M5 saves are deliberately not migrated. Covers both the
 * v4M3.1 schema delta (Empire.teamId — back-filled at deserialise so
 * saves *could* have loaded, but the SAVE_BLOB_VERSION bump was omitted
 * in that commit) and the v4M5.1 ShipTemplate / xlsx schema rework.
 * Players hold an Alpha 0.5 / v4M3-build tab open to continue any
 * in-progress save; v4M5 builds start fresh.
 */
/**
 * v4M5.3a — bumped 14 → 15 alongside SAVE_FORMAT_VERSION 15 → 16. New
 * `chargeUpTicksRemaining` + `jumpFuelCostPaid` fields on `ShipInstance`
 * make charge-up an on-map vulnerable phase per `HYPERSPACE.md §6`.
 * Pre-v15 blobs can't represent the new state — bump enforces fail-fast.
 */
/**
 * v4M5.3b1 — bumped 15 → 16 alongside SAVE_FORMAT_VERSION 16 → 17. New
 * `pendingJumpSystemId` + `pendingJumpArrivalAngle` fields on
 * `ShipInstance` for the continuous-Jump-Area dispatch model per
 * `HYPERSPACE.md §4`. v4M5.3b1 was additive (legacy `pendingJumpPointId`
 * stayed on the type).
 *
 * v4M5.3b3a — bumped 16 → 17 alongside SAVE_FORMAT_VERSION 17 → 18.
 * Legacy `pendingJumpPointId` dropped; `jumpTargetJumpPointId`
 * replaced by `jumpArrivalAngle: number | null`. Appliers now consume
 * `targetSystemId` + `arrivalAngle` natively; the v4M5.3b2
 * `resolveJumpPointForCommand` adapter is gone.
 *
 * v4M5.3b3c — bumped 17 → 18 alongside SAVE_FORMAT_VERSION 18 → 19.
 * `state.jumpPoints: Map<JumpPointId, JumpPoint>` retired entirely
 * (no more serialised entry). `StarSystem.adjacentSystemIds` collapsed to
 * `StarSystem.adjacentSystemIds: SystemId[]` — symmetric system-pair
 * adjacency rides on the StarSystem record itself.
 *
 * v4M4.e.1 — bumped 18 → 19 alongside SAVE_FORMAT_VERSION 19 → 20.
 * Weapons Platform 3 → 6 subType expansion: pre-v19 blobs may carry
 * `chassis: 'Energy Weapons Platform'` / `'Kinetic Weapons Platform'`
 * / `'Concussion Weapons Platform'` ShipInstance records that no
 * longer have backing `shipTemplates` entries. Hard break: pre-v19
 * blobs reject at deserialise rather than load with phantom ships.
 *
 * v4M9.1b — bumped 19 → 20 alongside SAVE_FORMAT_VERSION 20 → 21.
 * `state.missiles` renamed to `state.projectiles`; the serialised
 * field follows the same rename. Pre-v20 blobs carry `body.missiles`
 * which the new deserialiser does not consume — reject up front.
 *
 * v4M9.2 — bumped 20 → 21 alongside SAVE_FORMAT_VERSION 21 → 22.
 * Each entry in `body.projectiles` gained a `family: ProjectileFamily`
 * discriminant (`'Missile' | 'Torpedo'`). Pre-v21 blobs have entries
 * with no `family` field — the family-dispatch switch at advance time
 * would land on `undefined`. Hard break instead.
 *
 * v4M9.3 — bumped 21 → 22 alongside SAVE_FORMAT_VERSION 22 → 23.
 * `'Pulse'` joined the `ProjectileFamily` enum and Pulse migrated
 * from the instant-hit + TracerVfx model to the persistent-projectile
 * pipeline.
 *
 * v4M9.4 — bumped 22 → 23 alongside SAVE_FORMAT_VERSION 23 → 24.
 * `'Burst'` joined the `ProjectileFamily` enum; Burst weapons migrated
 * to the persistent-projectile pipeline with multi-round stagger
 * (each fire event spawns N rounds with progressive `launchedAtTick`).
 *
 * v4M9.5 — bumped 23 → 24 alongside SAVE_FORMAT_VERSION 24 → 25.
 * `state.beams` became persistent: in-flight `BeamFire` channels now
 * round-trip through the save body as a `[number, BeamFire][]` tuple
 * array (same shape as `projectiles`). Pre-v24 blobs have no `beams`
 * field; loading them under v4M9.5 would drop any mid-sustain beams.
 *
 * MFix13.3e — bumped 24 → 25 alongside SAVE_FORMAT_VERSION 25 → 26
 * when squadrons gained `isReturning?: boolean`. Deployed squadrons
 * with a committed return (manual recall in-flight, or auto-recall
 * -at-low in-flight) carry the flag through the ship-shape pass —
 * loading a pre-v25 blob would silently drop the committed return
 * and the squadron would switch back to "open to pursuit" mid-flight.
 *
 * MFix19 — bumped 25 → 26 alongside SAVE_FORMAT_VERSION 26 → 27 when
 * BeamFire gained `slotIndex`. In-flight beams from pre-v26 blobs
 * are deserialised with slotIndex 0 (the old single-channel render
 * behaviour) — the multi-emitter fan visual will return on the next
 * fire-event after save-load.
 *
 * MFix29.1 — bumped 26 → 27 alongside SAVE_FORMAT_VERSION 27 → 28
 * when Projectile gained `slotPerp`. Pre-v27 projectiles deserialise
 * with `slotPerp = 0` so the pulse render falls back to the firer's
 * centerline on resume; the multi-slot fan returns on next fire.
 *
 * v4M8.1 — bumped 27 → 28 alongside SAVE_FORMAT_VERSION 28 → 29 for
 * the AI behaviour restructure: Empire field rename (`aiBehaviour` →
 * `aiArchetype`), legacy six-value behaviour union dropped (no clean
 * Balanced/Economist mapping), AiDifficulty restructured (Normal →
 * Medium; Brutal added). Pre-v28 blobs reject up front rather than
 * attempting an opinionated hand-migration.
 */
// v4M11.1 — bumped 29 → 30 alongside SAVE_FORMAT_VERSION 30 → 31 for
// the in-system-fog discovered sets per `IN_SYSTEM_FOG.md §3`. Pre-v30
// blobs reconstruct the new sets from `scoutedSystemIds × planets/env
// in each scouted system` on load — see `deserialize.ts` for the
// reconstruction path.
//
// MFix42.8a (2026-06-02) — bumped 31 → 32 alongside SAVE_FORMAT_VERSION
// 33 → 34 for the new `Empire.parkedResearchProgress` (Map<string, number>)
// + `Empire.researchQueue` (string[]) fields. Hard save break at the
// version gate (validateBlobShape rejects on strict `!==`); the empty-
// Map / empty-array defaults in `deserializeEmpire` are deserialiser-
// side robustness against missing keys, not cross-version migration.
// Alpha 0.6 saves were already broken at MFix42.2 (Missile Weapons
// Platform removal), so this bump imposes no incremental player-side
// cost. If the helper rewrite at MFix42.8b were ever fed a pre-v32
// blob through some side path, the empty-Map seed would mean it
// behaves identically to the pre-MFix42.8 reset-to-0 semantics.
export const SAVE_BLOB_VERSION = 32;

/**
 * Serialised Empire — Sets converted to arrays, everything else as-is.
 * `scoutedSystemIds` (MFix6.3) is the second Set field on Empire and
 * follows the same pattern as `researchedTechs`. v4M11.1 added two
 * more Set fields — `discoveredPlanetIds` + `discoveredEnvironmentIds`
 * — for the in-system entity-level fog of war (`IN_SYSTEM_FOG.md`).
 */
export interface SerializedEmpire
  extends Omit<
    Empire,
    | 'researchedTechs'
    | 'scoutedSystemIds'
    | 'discoveredPlanetIds'
    | 'discoveredEnvironmentIds'
    | 'parkedResearchProgress'
  > {
  researchedTechs: string[];
  scoutedSystemIds: number[];
  discoveredPlanetIds: number[];
  discoveredEnvironmentIds: number[];
  /**
   * MFix42.8a (2026-06-02) — parked research progress flattened from a
   * Map<techName, rp> to a `[techName, rp][]` tuple array. Empty array
   * when the map is empty (saves taken before any tech-switch). `Empire.
   * researchQueue` is plain `string[]` and rides through `Omit`
   * untouched.
   */
  parkedResearchProgress: Array<[string, number]>;
}

/**
 * Serialised Planet — `influence: Map<EmpireId, number>` is flattened
 * to an array of `[empireId, points]` pairs (same pattern as
 * `SerializedTeamVision`'s nested-Map flattening). Other Planet fields
 * are JSON-clean and ride through as-is. v3M21.1.
 */
export interface SerializedPlanet extends Omit<Planet, 'influence'> {
  influence: Array<[number, number]>;
}

/** Serialised TeamVision — outer Map of inner Map flattened to array of arrays. */
export type SerializedTeamVision = Array<
  [number, Array<[number, VisibilityRecord]>]
>;

/**
 * v4M10.1 — serialised FoW snapshot. Nested Maps (planets, structures,
 * planet.influence) all flatten to array tuples per the existing
 * `SerializedPlanet.influence` pattern.
 */
export interface SerializedPlanetFoWState extends Omit<PlanetFoWState, 'influence'> {
  influence: Array<[number, number]>;
}

export interface SerializedFogOfWarSnapshot {
  lastSeenTick: number;
  planets: Array<[number, SerializedPlanetFoWState]>;
  structures: Array<[number, StructureFoWState]>;
}

/**
 * Outer per-empire map of inner per-system snapshots, flattened.
 * Shape: `Array<[empireId, Array<[systemId, SerializedFogOfWarSnapshot]>]>`.
 */
export type SerializedFogOfWarSnapshots = Array<
  [number, Array<[number, SerializedFogOfWarSnapshot]>]
>;

/**
 * The shape that ends up in localStorage / on disk. Plain JSON: no
 * Maps, no Sets, no class instances.
 */
export interface SerializedGameState {
  version: number;
  tick: number;
  rng: RngState;
  nextEntityId: number;
  empires: Array<[number, SerializedEmpire]>;
  planets: Array<[number, SerializedPlanet]>;
  systems: Array<[number, StarSystem]>;
  /** v3M18.1 — explicit player-authored Warp Gate connections. */
  warpGateLinks: Array<[number, WarpGateLink]>;
  ships: Array<[number, ShipInstance]>;
  /**
   * v3M28.1 — persistent missile entities in flight. Each entry round-
   * trips intact (no nested Maps/Sets on the Missile type, so a plain
   * tuple serialise is sufficient). Empty array for saves taken
   * outside of combat.
   *
   * v4M9.1b — field renamed from `missiles` to `projectiles` in lockstep
   * with the runtime `state.projectiles` rename. SAVE_BLOB_VERSION
   * bumped to 20 to reject pre-rename blobs up front.
   *
   * v4M9.2 — element type widened from `Missile` to `Projectile` (the
   * latter is now the canonical name and carries the `family`
   * discriminant). `Missile` survives as a type alias of `Projectile`
   * so this declaration would still type-check either way; using
   * `Projectile` makes the family-aware intent explicit.
   */
  projectiles: Array<[number, Projectile]>;
  /**
   * v4M9.5 — in-flight beam-sustain channels. Each `BeamFire` carries
   * only flat number/string fields so a plain tuple serialise is
   * sufficient (same shape as `projectiles`). Empty array for saves
   * taken outside of combat.
   */
  beams: Array<[number, BeamFire]>;
  structures: Array<[number, Structure]>;
  environment: Array<[number, EnvironmentEntity]>;
  teamVision: SerializedTeamVision;
  /**
   * v4M10.1 — per-empire Fog of War snapshots per `docs/FOW.md §3.3`.
   * Lenient back-compat on load: pre-v30 blobs lacking this field
   * deserialise with an empty map seed (non-load-bearing for sim
   * correctness; first tick's capture pass re-populates).
   */
  fogOfWarSnapshots: SerializedFogOfWarSnapshots;
  playerEmpireId: number;
  /**
   * v3M12 — saved/loaded so a finished game stays finished. Older
   * blobs without this field default to `'in-progress'` on load.
   */
  gameOutcome: 'in-progress' | 'victory' | 'defeat';
}

/**
 * Save-blob header. Player-facing slot pickers read these fields to
 * render a meaningful "what's in this slot" line without parsing the
 * full body.
 */
export interface SaveBlobHeader {
  formatVersion: number;
  /** Wall-clock timestamp at save time (ms since epoch). */
  savedAt: number;
  /** Sim tick at save time — useful for "Game time: 12:34" displays. */
  tick: number;
  /** Player's empire id (so the slot picker can show the player's race). */
  playerEmpireId: number;
  /** Number of non-player empires at save time. */
  aiOpponentCount: number;
  /**
   * Optional player-supplied label (e.g. "before invasion"). Slots use
   * this in the UI; quicksave leaves it null.
   */
  label: string | null;
  /**
   * v3M12 — surfaced at header level so the save slot picker can show
   * a victory / defeat badge without parsing the full body. Older
   * blobs without this field render as in-progress.
   */
  gameOutcome: 'in-progress' | 'victory' | 'defeat';
}

export interface SaveBlob {
  header: SaveBlobHeader;
  body: SerializedGameState;
}

/** Replace Infinity ammo with the reserved sentinel for JSON safety. */
function serializeShip(s: ShipInstance): ShipInstance {
  return {
    ...s,
    weaponSlots: s.weaponSlots.map((w) => ({
      cooldownTicks: w.cooldownTicks,
      ammoRemaining: w.ammoRemaining === Infinity ? INF_AMMO_SENTINEL : w.ammoRemaining,
    })),
  };
}

/** Convert one Empire into its serialised shape (Set → array). */
function serializeEmpire(e: Empire): SerializedEmpire {
  return {
    id: e.id,
    name: e.name,
    raceId: e.raceId,
    isPlayer: e.isPlayer,
    resources: { ...e.resources },
    planetIds: [...e.planetIds],
    researchedTechs: [...e.researchedTechs],
    activeResearch: e.activeResearch ? { ...e.activeResearch } : null,
    shipIds: [...e.shipIds],
    color: e.color,
    aiDifficulty: e.aiDifficulty,
    // v4M8.1 — Empire field renamed `aiBehaviour` → `aiArchetype`.
    // SerializedEmpire re-keyed; SAVE_BLOB_VERSION + SAVE_FORMAT_VERSION
    // bumps reject pre-rename blobs at the deserialise gate.
    aiArchetype: e.aiArchetype,
    // v4M3.1 — team membership round-trips verbatim. `null` for the
    // pre-v14 legacy "all solo" default; non-null integer for teamed
    // empires. Save-format version bumped to 14 to mark the schema.
    teamId: e.teamId,
    scoutedSystemIds: [...e.scoutedSystemIds],
    // v4M11.1 — in-system fog discovered sets per `IN_SYSTEM_FOG.md
    // §3`. Same flatten pattern as `scoutedSystemIds`.
    discoveredPlanetIds: [...e.discoveredPlanetIds],
    discoveredEnvironmentIds: [...e.discoveredEnvironmentIds],
    // MFix42.8a — parked research progress Map flattened to tuples;
    // researchQueue rides through as string[] via the spread of fields
    // not Omit'd above. Empty for empires that never used switch / queue.
    parkedResearchProgress: [...e.parkedResearchProgress.entries()],
    researchQueue: [...e.researchQueue],
    diplomaticFocus: e.diplomaticFocus,
  };
}

/**
 * v3M21.1 — Convert a live Planet (with `influence: Map`) into a
 * JSON-safe SerializedPlanet (with `influence: Array<[id, points]>`).
 * All other fields ride through unchanged via spread.
 */
function serializePlanet(p: Planet): SerializedPlanet {
  return {
    ...p,
    influence: [...p.influence.entries()],
  };
}

function serializeTeamVision(
  teamVision: GameState['teamVision'],
): SerializedTeamVision {
  const out: SerializedTeamVision = [];
  for (const [teamId, inner] of teamVision) {
    const innerArr: Array<[number, VisibilityRecord]> = [];
    for (const [shipId, rec] of inner) innerArr.push([shipId, { ...rec }]);
    out.push([teamId, innerArr]);
  }
  return out;
}

/**
 * v4M10.1 — flatten a live PlanetFoWState's `influence` Map into the
 * `Array<[id, points]>` array shape, mirroring `serializePlanet`.
 */
function serializePlanetFoWState(p: PlanetFoWState): SerializedPlanetFoWState {
  return {
    ...p,
    influence: [...p.influence.entries()],
  };
}

/**
 * v4M10.1 — flatten the nested per-empire / per-system / per-planet
 * Maps inside `state.fogOfWarSnapshots` into the array-of-arrays shape
 * per `docs/FOW.md §3.3`.
 */
function serializeFogOfWarSnapshots(
  snapshots: GameState['fogOfWarSnapshots'],
): SerializedFogOfWarSnapshots {
  const out: SerializedFogOfWarSnapshots = [];
  for (const [empireId, perSystem] of snapshots) {
    const perSystemArr: Array<[number, SerializedFogOfWarSnapshot]> = [];
    for (const [sysId, snap] of perSystem) {
      const planetsArr: Array<[number, SerializedPlanetFoWState]> = [];
      for (const [pid, planetState] of snap.planets) {
        planetsArr.push([pid, serializePlanetFoWState(planetState)]);
      }
      const structuresArr: Array<[number, StructureFoWState]> = [];
      for (const [sid, structureState] of snap.structures) {
        structuresArr.push([sid, { ...structureState }]);
      }
      perSystemArr.push([
        sysId,
        {
          lastSeenTick: snap.lastSeenTick,
          planets: planetsArr,
          structures: structuresArr,
        },
      ]);
    }
    out.push([empireId, perSystemArr]);
  }
  return out;
}

/**
 * Serialise a live GameState into a JSON-safe SaveBlob. The blob is the
 * full input to JSON.stringify — write `JSON.stringify(blob)` to persist.
 *
 * `label` is the player's slot label (named slots) or null (quicksave).
 */
export function serializeGameState(
  state: GameState,
  opts: { label: string | null },
): SaveBlob {
  // Body. Maps walk in insertion order; that's stable in v8 / SpiderMonkey
  // for integer keys, but we don't rely on order semantically — the
  // deserialiser rebuilds Maps from the entry list and order doesn't
  // affect sim behaviour.
  const body: SerializedGameState = {
    version: state.version,
    tick: state.tick,
    rng: { s: state.rng.s },
    nextEntityId: state.nextEntityId,
    empires: [...state.empires.entries()].map(([id, e]) => [id, serializeEmpire(e)]),
    planets: [...state.planets.entries()].map(([id, p]) => [id, serializePlanet(p)]),
    systems: [...state.systems.entries()],
    warpGateLinks: [...state.warpGateLinks.entries()],
    ships: [...state.ships.entries()].map(([id, s]) => [id, serializeShip(s)]),
    projectiles: [...state.projectiles.entries()],
    beams: [...state.beams.entries()],
    structures: [...state.structures.entries()],
    environment: [...state.environment.entries()],
    teamVision: serializeTeamVision(state.teamVision),
    fogOfWarSnapshots: serializeFogOfWarSnapshots(state.fogOfWarSnapshots),
    playerEmpireId: state.playerEmpireId,
    gameOutcome: state.gameOutcome,
  };

  // v3M21.1 — exclude the synthetic neutral empire from the AI count.
  // Header surfaces "real AI opponents" only; the neutral row is an
  // implementation detail of the diplomacy system, not a competing AI.
  const aiOpponentCount = [...state.empires.values()].filter(
    (e) => !e.isPlayer && e.id !== EMPIRE_NEUTRAL_ID,
  ).length;

  const header: SaveBlobHeader = {
    formatVersion: SAVE_BLOB_VERSION,
    savedAt: Date.now(),
    tick: state.tick,
    playerEmpireId: state.playerEmpireId,
    aiOpponentCount,
    label: opts.label,
    gameOutcome: state.gameOutcome,
  };

  return { header, body };
}
